import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { clientService } from '../../../services/clientService';

const STATUS_CONFIG = {
  valid: { label: 'Valid', color: 'text-emerald-600', bg: 'bg-emerald-100', icon: 'CheckCircle2' },
  'expiring-soon': { label: 'Expiring Soon', color: 'text-amber-600', bg: 'bg-amber-100', icon: 'Clock' },
  'expiring-critical': { label: 'Critical', color: 'text-orange-600', bg: 'bg-orange-100', icon: 'AlertTriangle' },
  expired: { label: 'Expired', color: 'text-red-600', bg: 'bg-red-100', icon: 'XCircle' },
};

const CATEGORY_LABELS = {
  rams: 'H&S Docs',
  insurance: 'Insurance',
  training: 'Training',
  plant: 'Plant',
};

const ClientDocuments = ({ siteId, siteName }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    if (siteId) loadDocuments();
  }, [siteId, categoryFilter, statusFilter]);

  const loadDocuments = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await clientService?.getSiteDocuments(siteId, {
        category: categoryFilter,
        status: statusFilter,
      });
      if (err) throw err;
      setDocuments(data || []);
    } catch (err) {
      console.error('[ClientDocuments] load documents failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setError(`Permission denied loading documents: ${err?.message}`);
      } else {
        setError(`Failed to load documents: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    if (!doc?.file_path) return;
    setDownloadingId(doc?.id);
    try {
      const { url, error: err } = await clientService?.getDocumentUrl(doc?.file_path);
      if (err) throw err;
      if (url) {
        const a = document?.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a?.click();
      }
    } catch (err) {
      console.error('[ClientDocuments] download failed:', { id: doc?.id, code: err?.code, message: err?.message });
      const msg = err?.code === 'PGRST301'
        ? `Permission denied: ${err?.message}`
        : `Download failed: ${err?.message || 'Unknown error'}`;
      setError(msg);
    } finally {
      setDownloadingId(null);
    }
  };

  const categories = ['all', 'rams', 'insurance', 'training', 'plant'];
  const statuses = ['all', 'valid', 'expiring-soon', 'expiring-critical', 'expired'];
  const hasFilters = categoryFilter !== 'all' || statusFilter !== 'all';

  return (
    <div className="bg-card rounded-xl border border-border">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="FileText" size={18} className="text-accent" />
          <h3 className="font-semibold text-foreground">Shared Documents</h3>
          {documents?.length > 0 && (
            <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{documents?.length}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 bg-muted/50 px-2.5 py-1 rounded-lg">
          <Icon name="Lock" size={12} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">View only</span>
        </div>
      </div>

      {/* Filters */}
      <div className="px-5 py-3 border-b border-border bg-muted/20 flex flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground font-medium">Category:</label>
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e?.target?.value)}
            className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {categories?.map(c => (
              <option key={c} value={c}>{c === 'all' ? 'All Categories' : CATEGORY_LABELS?.[c] || c}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground font-medium">Status:</label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e?.target?.value)}
            className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {statuses?.map(s => (
              <option key={s} value={s}>{s === 'all' ? 'All Statuses' : STATUS_CONFIG?.[s]?.label || s}</option>
            ))}
          </select>
        </div>
        {hasFilters && (
          <button
            onClick={() => { setCategoryFilter('all'); setStatusFilter('all'); }}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Clear
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3]?.map(i => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl border border-border animate-pulse">
                <div className="w-10 h-10 rounded-lg bg-muted flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-3 bg-muted rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-10">
            <Icon name="AlertCircle" size={24} className="text-red-400 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        ) : documents?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FileText" size={32} className="text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm font-medium text-foreground mb-1">No documents found</p>
            <p className="text-xs text-muted-foreground">
              {hasFilters ? 'Try adjusting your filters.' : 'No documents have been uploaded for this site yet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {documents?.map(doc => {
              const statusCfg = STATUS_CONFIG?.[doc?.status] || STATUS_CONFIG?.valid;
              return (
                <div key={doc?.id} className="flex items-center gap-3 p-4 rounded-xl border border-border hover:bg-muted/30 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="FileText" size={18} className="text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-semibold text-foreground truncate">{doc?.title}</p>
                      <span className={`flex-shrink-0 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg?.bg} ${statusCfg?.color}`}>
                        <Icon name={statusCfg?.icon} size={10} />
                        {statusCfg?.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">
                        {CATEGORY_LABELS?.[doc?.category] || doc?.category}
                      </span>
                      {doc?.owner && (
                        <span className="text-xs text-muted-foreground">Owner: {doc?.owner}</span>
                      )}
                      {doc?.expiry_date && (
                        <span className="text-xs text-muted-foreground">
                          Expires: {new Date(doc?.expiry_date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </div>
                  {doc?.file_path && (
                    <button
                      onClick={() => handleDownload(doc)}
                      disabled={downloadingId === doc?.id}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                    >
                      {downloadingId === doc?.id ? (
                        <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Icon name="Download" size={13} />
                      )}
                      View
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDocuments;
